import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import EmblaCarousel, {
  EmblaCarouselType,
  EmblaOptionsType,
  EmblaPluginType,
} from 'embla-carousel';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { ProductCardGalleryViewportObserver } from './viewport-observer.js';

type ProductCardGalleryRefs = {
  viewport?: HTMLElement;
  prevBtn?: HTMLButtonElement;
  nextBtn?: HTMLButtonElement;
};

/**
 * Fine pointer with hover (desktop mouse). Tablets and phones init on enter-viewport
 * so swipe works without a hover. Desktop waits for pointerenter so a grid of cards
 * does not construct Embla during first paint.
 */
const hoverFineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

class ProductCardGallery extends Component<ProductCardGalleryRefs> {
  #emblaApi: EmblaCarouselType | null = null;
  #inViewportObserver: MutationObserver | null = null;
  #abort: AbortController | null = null;
  #mounted = false;
  #slidesHydrated = false;
  #prefetchScheduled = false;
  #pointerInside = false;
  #suppressLinkClick = false;
  #engaged = false;
  #pressedForDrag = false;
  #userScrolled = false;
  #programmaticScroll = false;
  #waitingForHoverImage = false;
  #hoverReleaseTimer = 0;
  #productLink: HTMLAnchorElement | null = null;
  #onMediaChange = () => this.#syncEmbla();
  #handleLinkClick = (event: MouseEvent) => this.#onLinkClick(event);
  #onPointerEnter = () => {
    this.#pointerInside = true;
    window.clearTimeout(this.#hoverReleaseTimer);
    if (!this.#wantsHoverSlider()) return;
    this.#beginHoverPreview();
  };
  #onPointerLeave = () => {
    this.#pointerInside = false;
    this.#endHoverPreview();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#mount();
  }

  /**
   * Collection load-more parses the next page with DOMParser and moves the
   * nodes into the live grid. Adoption can skip a second connectedCallback,
   * so re-bind once the element is in the visible document.
   */
  adoptedCallback() {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      if (!this.#mounted) {
        this.#mount();
        return;
      }
      this.#deferSync();
    });
  }

  disconnectedCallback() {
    this.#unmount();
    super.disconnectedCallback();
  }

  /**
   * Section Rendering morph restores server HTML (inert template, no Embla
   * styles). Drop the stale carousel and let the viewport observer start again.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#slidesHydrated = this.querySelector('template[data-product-card-slides]') == null;
    this.#destroyEmbla();
    this.#deferSync();
  }

  scrollPrev(event?: Event) {
    this.#onArrow(event, 'prev');
  }

  scrollNext(event?: Event) {
    this.#onArrow(event, 'next');
  }

  #mount() {
    if (this.#mounted) return;
    this.#mounted = true;

    if (this.closest('rhm-carousel-component')) {
      this.setAttribute('data-nested', '');
    }

    this.#productLink =
      this.querySelector('a.product-card-image__link') ??
      this.closest('a.product-card-image__link');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#productLink?.addEventListener('click', this.#handleLinkClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#onMediaChange, { signal });
    hoverFineQuery.addEventListener('change', this.#onMediaChange, { signal });
    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('click', (event) => this.#onGalleryClick(event), { signal });
    // Capture runs before Embla's bubble listener on the viewport, so the same
    // mousedown that starts a swipe can be picked up after we create the carousel.
    this.addEventListener('mousedown', (event) => this.#onMouseDown(event), {
      signal,
      capture: true,
    });
    this.addEventListener('wheel', (event) => this.#onWheel(event), {
      signal,
      capture: true,
      passive: true,
    });

    ProductCardGalleryViewportObserver.getInstance().observe(this);
    this.#watchInViewport();
    this.#deferSync();
  }

  #unmount() {
    this.#mounted = false;
    this.#pointerInside = false;
    this.#prefetchScheduled = false;
    window.clearTimeout(this.#hoverReleaseTimer);
    this.#abort?.abort();
    this.#abort = null;
    this.#productLink = null;
    this.#inViewportObserver?.disconnect();
    this.#inViewportObserver = null;
    ProductCardGalleryViewportObserver.getInstance().unobserve(this);
    this.#destroyEmbla();
  }

  #deferSync() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#syncEmbla();
    });
  }

  #onLinkClick(event: MouseEvent) {
    if (!this.#suppressLinkClick) return;
    event.preventDefault();
    this.#suppressLinkClick = false;
  }

  #onGalleryClick(event: Event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.product-card-gallery__btn');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    if (button.classList.contains('product-card-gallery__btn--prev')) this.scrollPrev(event);
    else if (button.classList.contains('product-card-gallery__btn--next')) this.scrollNext(event);
  }

  #onArrow(event: Event | undefined, direction: 'prev' | 'next') {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.#emblaApi && !this.#emblaIsCurrent()) this.#destroyEmbla();
    this.#hydrateSlides();
    if (!this.#emblaApi && this.#slideCount() > 1) this.#initEmbla();
    if (this.#isHoverPreview()) this.#engageFromSecondImage();
    if (direction === 'prev') this.#emblaApi?.scrollPrev();
    else this.#emblaApi?.scrollNext();
  }

  #onMouseDown(event: MouseEvent) {
    this.#activateHoverSlider();
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.product-card-gallery__btn')) return;
    if (this.#isNested() || !this.#isHoverPreview()) return;
    this.#pressedForDrag = true;
    this.#userScrolled = false;
    this.#engageFromSecondImage();
  }

  /**
   * Desktop hover fades the second image over the first. The carousel stays on
   * the first slide until an arrow, drag, or trackpad swipe continues from the
   * second image. Leaving fades back, or jumps back when a real slide happened.
   */
  #beginHoverPreview() {
    this.#activateHoverSlider();
    const reveal = () => {
      if (!this.#pointerInside || this.#engaged) return;
      if (this.#slideCount() < 2) return;
      this.classList.add('is-hover-preview');
      this.#updateUi();
    };
    const image = this.#secondImage();
    if (image && !image.complete) {
      if (this.#waitingForHoverImage) return;
      this.#waitingForHoverImage = true;
      image.addEventListener(
        'load',
        () => {
          this.#waitingForHoverImage = false;
          reveal();
        },
        { once: true },
      );
      return;
    }
    reveal();
  }

  #endHoverPreview() {
    if (this.#engaged) {
      this.#engaged = false;
      this.#pressedForDrag = false;
      this.#userScrolled = false;
      this.classList.remove('is-engaged', 'is-hover-preview');
      window.clearTimeout(this.#hoverReleaseTimer);
      this.#jumpTo(0);
      this.#updateUi();
      return;
    }

    const release = () => {
      if (this.#pointerInside || this.#engaged) return;
      this.classList.remove('is-hover-preview');
      this.#updateUi();
    };
    const slide = this.#secondSlide();
    if (slide) {
      slide.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'opacity') return;
          release();
        },
        { once: true },
      );
    }
    window.clearTimeout(this.#hoverReleaseTimer);
    this.#hoverReleaseTimer = window.setTimeout(release, 250);
  }

  #isHoverPreview(): boolean {
    return this.classList.contains('is-hover-preview') && !this.#engaged;
  }

  #engageFromSecondImage() {
    const embla = this.#emblaApi;
    if (this.#engaged || !embla) return;
    if (embla.scrollSnapList().length > 1 && embla.selectedScrollSnap() === 0) {
      this.#jumpTo(1);
    }
    this.#engaged = true;
    this.classList.add('is-engaged');
    this.#updateUi();
  }

  #jumpTo(index: number) {
    const embla = this.#emblaApi;
    if (!embla) return;
    this.#programmaticScroll = true;
    embla.scrollTo(index, true);
    this.#programmaticScroll = false;
  }

  #secondSlide(): HTMLElement | null {
    const slide = this.#container()?.querySelectorAll(':scope > .embla__slide')[1];
    return slide instanceof HTMLElement ? slide : null;
  }

  #secondImage(): HTMLImageElement | null {
    const image = this.#secondSlide()?.querySelector('img');
    return image instanceof HTMLImageElement ? image : null;
  }

  #watchInViewport() {
    this.#inViewportObserver?.disconnect();
    this.#inViewportObserver = new MutationObserver(() => {
      this.#syncEmbla();
      this.#schedulePrefetch();
    });
    this.#inViewportObserver.observe(this, {
      attributes: true,
      attributeFilter: ['in-viewport', 'data-nested'],
    });
  }

  #isNested(): boolean {
    return this.hasAttribute('data-nested');
  }

  #isFineHover(): boolean {
    return hoverFineQuery.matches;
  }

  #wantsHoverSlider(): boolean {
    return mediaQueryLarge.matches && this.#isFineHover() && this.dataset.hoverImage === 'true';
  }

  /**
   * Touch / coarse pointers start the carousel when the card is near the viewport.
   * Desktop fine pointers wait for idle prefetch or the first hover / mousedown,
   * so the first paint is not blocked and a mouse drag still has a listener.
   */
  #shouldAutoInit(): boolean {
    if (!this.hasAttribute('in-viewport')) return false;
    if (this.#isNested() && !mediaQueryLarge.matches) return false;

    if (mediaQueryLarge.matches) {
      if (this.#isFineHover()) return false;
      return this.dataset.hoverImage === 'true' || this.dataset.mobileCarousel === 'true';
    }

    return this.dataset.mobileCarousel === 'true';
  }

  #keepAlive(): boolean {
    if (!this.#emblaApi || !this.#wantsHoverSlider()) return false;
    return this.hasAttribute('in-viewport') || this.#pointerInside;
  }

  #syncEmbla() {
    if (this.#emblaApi && !this.#emblaIsCurrent()) this.#destroyEmbla();

    if (!this.#shouldAutoInit()) {
      if (!this.#keepAlive()) this.#destroyEmbla();
      this.#schedulePrefetch();
      return;
    }

    this.#hydrateSlides();
    if (this.#slideCount() < 2) {
      this.#destroyEmbla();
      return;
    }

    if (!this.#emblaApi) this.#initEmbla();
    else this.#emblaApi.reInit();
  }

  #emblaIsCurrent(): boolean {
    const viewport = this.#viewport();
    return Boolean(this.#emblaApi && viewport && this.#emblaApi.rootNode() === viewport);
  }

  #activateHoverSlider() {
    if (!this.#wantsHoverSlider() && !this.#shouldAutoInit()) return;
    if (this.#emblaApi && !this.#emblaIsCurrent()) this.#destroyEmbla();
    this.#hydrateSlides();
    if (this.#slideCount() < 2) return;
    if (!this.#emblaApi) this.#initEmbla();
  }

  /**
   * Insert deferred slides during idle time once the card is near the viewport,
   * then start Embla so a mouse drag is already listening. The template is inert,
   * so these images are not discovered during the initial parse.
   */
  #schedulePrefetch() {
    if (this.#prefetchScheduled) return;
    if (this.#slidesHydrated && this.#emblaApi) return;
    if (!this.hasAttribute('in-viewport')) return;
    if (!this.#wantsHoverSlider()) return;
    if (this.#isNested()) return;
    if (this.ownerDocument !== document) return;

    this.#prefetchScheduled = true;
    const run = () => {
      this.#prefetchScheduled = false;
      if (!this.isConnected || !this.hasAttribute('in-viewport')) return;
      this.#activateHoverSlider();
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 1500 });
    } else {
      window.setTimeout(run, 200);
    }
  }

  #hydrateSlides() {
    const template = this.querySelector('template[data-product-card-slides]');
    if (!(template instanceof HTMLTemplateElement)) {
      this.#slidesHydrated = true;
      return;
    }

    const container = this.#container();
    if (!container) return;

    container.append(template.content.cloneNode(true));
    template.remove();
    this.#slidesHydrated = true;
  }

  #slideCount(): number {
    return this.#container()?.querySelectorAll(':scope > .embla__slide').length ?? 0;
  }

  #container(): HTMLElement | null {
    return this.querySelector('.embla__container');
  }

  #viewport(): HTMLElement | null {
    const fromRef = this.refs.viewport;
    if (fromRef instanceof HTMLElement) return fromRef;
    return this.querySelector('.product-card-gallery__viewport');
  }

  #button(name: 'prev' | 'next'): HTMLButtonElement | null {
    const fromRef = name === 'prev' ? this.refs.prevBtn : this.refs.nextBtn;
    if (fromRef instanceof HTMLButtonElement) return fromRef;
    return this.querySelector(`.product-card-gallery__btn--${name}`);
  }

  #onWheel(event: WheelEvent) {
    if (this.#isNested()) return;
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    this.#activateHoverSlider();
    if (this.#isHoverPreview()) this.#engageFromSecondImage();
  }

  #initEmbla() {
    const viewport = this.#viewport();
    if (!viewport || this.#emblaApi) return;
    if (this.#slideCount() < 2) return;

    const nested = this.#isNested();
    const options: EmblaOptionsType = {
      align: 'start',
      containScroll: 'trimSnaps',
      // Mouse and touch. Nested cards leave dragging to the parent carousel.
      watchDrag: !nested,
      duration: prefersReducedMotion() ? 0 : 25,
    };

    this.classList.add('is-ready');
    this.#emblaApi = EmblaCarousel(
      viewport,
      options,
      nested ? [] : [WheelGesturesPlugin() as EmblaPluginType],
    );
    // Delay grab→grabbing until the user actually scrolls (Horizon DRAG_THRESHOLD intent).
    // Adding is-dragging on every pointerDown makes tiny clicks look like drag and hurts desktop click UX.
    this.#emblaApi.on('pointerDown', () => {
      this.#suppressLinkClick = false;
    });
    this.#emblaApi.on('pointerUp', () => {
      this.classList.remove('is-dragging');
      if (this.#pressedForDrag && !this.#userScrolled) {
        this.#engaged = false;
        this.classList.remove('is-engaged');
        this.#jumpTo(0);
        if (this.#pointerInside) this.classList.add('is-hover-preview');
        this.#updateUi();
      }
      this.#pressedForDrag = false;
      this.#userScrolled = false;
    });
    this.#emblaApi.on('scroll', () => {
      if (this.#programmaticScroll) return;
      this.#userScrolled = true;
      this.#suppressLinkClick = true;
      if (!this.#isNested()) this.classList.add('is-dragging');
    });
    this.#emblaApi.on('select', () => this.#updateUi());
    this.#emblaApi.on('reInit', () => this.#updateUi());
    this.#updateUi();
  }

  #destroyEmbla() {
    this.#emblaApi?.destroy();
    this.#emblaApi = null;
    this.#suppressLinkClick = false;
    this.#engaged = false;
    this.#pressedForDrag = false;
    this.#userScrolled = false;
    this.#programmaticScroll = false;
    this.classList.remove('is-ready', 'is-dragging', 'is-engaged', 'is-hover-preview');
    this.#resetAriaHidden();
    this.#updateButtons();
  }

  #updateUi() {
    this.#updateAriaHidden();
    this.#updateButtons();
  }

  #updateButtons() {
    const prev = this.#button('prev');
    const next = this.#button('next');
    const arrows = this.querySelector('.product-card-gallery__arrows');
    if (!(arrows instanceof HTMLElement)) return;

    const count = this.#slideCount();
    // Before hydrate count is 1 (deferred slides in <template>). After hydrate,
    // never show inert arrows on a one-slide card.
    if (count < 2) {
      arrows.hidden = true;
      if (prev) prev.disabled = true;
      if (next) next.disabled = true;
      return;
    }

    if (this.#isHoverPreview()) {
      arrows.hidden = false;
      if (prev) prev.disabled = false;
      if (next) next.disabled = count < 3;
      return;
    }

    const paging = Boolean(this.#emblaApi && this.#emblaApi.scrollSnapList().length > 1);
    if (!paging) {
      arrows.hidden = true;
      return;
    }

    arrows.hidden = false;
    if (prev) prev.disabled = !this.#emblaApi?.canScrollPrev();
    if (next) next.disabled = !this.#emblaApi?.canScrollNext();
  }

  #updateAriaHidden() {
    if (!this.#emblaApi && !this.#isHoverPreview()) return;

    const selected = this.#isHoverPreview() ? 1 : (this.#emblaApi?.selectedScrollSnap() ?? 0);
    const slides = this.#container()?.querySelectorAll(':scope > .embla__slide');
    slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === selected ? 'false' : 'true');
    });
  }

  #resetAriaHidden() {
    const slides = this.#container()?.querySelectorAll(':scope > .embla__slide');
    slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });
  }
}

if (!customElements.get('product-card-gallery')) {
  customElements.define('product-card-gallery', ProductCardGallery);
}
